import { useState } from "react";
import { useRoastDetail, addComment, addRewrite } from "@/hooks/useStore";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScoreBadge, OverallScore } from "@/components/ScoreBadge";
import { VoteButton } from "@/components/VoteButton";
import { useApp } from "@/App";
import { toast } from "sonner";
import {
	ArrowLeft,
	ExternalLink,
	MessageSquare,
	GitFork,
	Send,
	Lightbulb,
	AlertTriangle,
	Clock,
} from "lucide-react";

function timeAgo(date: string): string {
	const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
	if (seconds < 60) return "just now";
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h ago`;
	const days = Math.floor(hours / 24);
	if (days < 30) return `${days}d ago`;
	return `${Math.floor(days / 30)}mo ago`;
}

const feedbackLabels: Record<string, { label: string; icon: string }> = {
	headline: { label: "Headline", icon: "H" },
	cta: { label: "Call to Action", icon: "!" },
	clarity: { label: "Clarity", icon: "C" },
	positioning: { label: "Positioning", icon: "P" },
	trustSignals: { label: "Trust Signals", icon: "T" },
};

export function RoastDetailPage({ roastId }: { roastId: string }) {
	const { roast, loading, refetch } = useRoastDetail(roastId);
	const { currentUser, navigate, showLogin } = useApp();
	const [comment, setComment] = useState("");
	const [rewriteCopy, setRewriteCopy] = useState("");
	const [rewriteExplanation, setRewriteExplanation] = useState("");
	const [forkingFrom, setForkingFrom] = useState<string | null>(null);
	const [forkOriginal, setForkOriginal] = useState("");
	const [submitting, setSubmitting] = useState(false);

	if (loading || !roast) {
		return (
			<div className="py-20 text-center text-muted-foreground">
				Loading roast details...
			</div>
		);
	}

	const handleComment = async () => {
		if (!currentUser) { showLogin(); return; }
		if (!comment.trim()) return;
		setSubmitting(true);
		await addComment(roastId, currentUser.id, comment.trim());
		setComment("");
		setSubmitting(false);
		toast.success("Comment added!");
		refetch();
	};

	const handleRewrite = async () => {
		if (!currentUser) { showLogin(); return; }
		if (!rewriteCopy.trim() || !rewriteExplanation.trim()) {
			toast.error("Please provide both rewritten copy and an explanation");
			return;
		}
		setSubmitting(true);
		await addRewrite(roastId, currentUser.id, rewriteCopy.trim(), rewriteExplanation.trim(), roast.copy);
		setRewriteCopy("");
		setRewriteExplanation("");
		setSubmitting(false);
		toast.success("Rewrite published!");
		refetch();
	};

	const handleFork = async () => {
		if (!currentUser) { showLogin(); return; }
		if (!rewriteCopy.trim() || !rewriteExplanation.trim()) {
			toast.error("Please provide both rewritten copy and an explanation");
			return;
		}
		setSubmitting(true);
		await addRewrite(roastId, currentUser.id, rewriteCopy.trim(), rewriteExplanation.trim(), forkOriginal, forkingFrom || undefined);
		setRewriteCopy("");
		setRewriteExplanation("");
		setForkingFrom(null);
		setForkOriginal("");
		setSubmitting(false);
		toast.success("Fork published!");
		refetch();
	};

	const startFork = (rewriteId: string, copy: string) => {
		setForkingFrom(rewriteId);
		setForkOriginal(copy);
		setRewriteCopy(copy);
		setRewriteExplanation("");
	};

	return (
		<div className="space-y-6">
			<button
				onClick={() => navigate({ name: "feed" })}
				className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
			>
				<ArrowLeft className="h-4 w-4" />
				Back to feed
			</button>

			{/* Original Copy */}
			<Card className="border-border/40 bg-card/50">
				<CardContent className="p-5">
					<div className="flex gap-4">
						<OverallScore score={roast.feedback.overallScore} />
						<div className="min-w-0 flex-1 space-y-2">
							{roast.url && (
								<div className="flex items-center gap-1.5">
									<ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
									<span className="text-sm text-blue-400">{roast.url}</span>
								</div>
							)}
							<p className="text-sm leading-relaxed text-foreground/90">
								"{roast.copy}"
							</p>
							<div className="flex items-center gap-3">
								<button
									onClick={() => roast.user && navigate({ name: "profile", id: roast.userId })}
									className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
								>
									<div className="flex h-5 w-5 items-center justify-center rounded-full bg-orange-500/20 text-[10px] font-bold text-orange-500">
										{roast.user?.username?.[0]?.toUpperCase() ?? "?"}
									</div>
									{roast.user?.username}
								</button>
								<span className="flex items-center gap-1 text-xs text-muted-foreground">
									<Clock className="h-3 w-3" />
									{timeAgo(roast.createdAt)}
								</span>
								<div className="ml-auto">
									<VoteButton targetId={roast.id} targetType="roast" initialCount={roast.upvotes} size="sm" />
								</div>
							</div>
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Summary */}
			<Card className="border-orange-500/20 bg-orange-500/5">
				<CardContent className="p-4">
					<p className="text-sm font-medium text-orange-300">{roast.feedback.summary}</p>
				</CardContent>
			</Card>

			{/* Detailed Feedback */}
			<div className="space-y-3">
				<h2 className="text-lg font-semibold">Detailed Feedback</h2>
				{Object.entries(roast.feedback)
					.filter(([key]) => feedbackLabels[key])
					.map(([key, val]) => {
						const v = val as { score: number; feedback: string; suggestion: string };
						const meta = feedbackLabels[key];
						return (
							<Card key={key} className="border-border/40 bg-card/50">
								<CardContent className="p-4">
									<div className="flex items-start gap-3">
										<ScoreBadge score={v.score} />
										<div className="min-w-0 flex-1 space-y-2">
											<h3 className="font-semibold">{meta.label}</h3>
											<div className="flex items-start gap-2 text-sm">
												<AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-400" />
												<p className="text-muted-foreground">{v.feedback}</p>
											</div>
											<div className="flex items-start gap-2 text-sm">
												<Lightbulb className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-400" />
												<p className="text-emerald-300/80">{v.suggestion}</p>
											</div>
										</div>
									</div>
								</CardContent>
							</Card>
						);
					})}
			</div>

			{/* Tabs: Comments & Rewrites */}
			<Tabs defaultValue="comments" className="space-y-4">
				<TabsList className="grid w-full grid-cols-2">
					<TabsTrigger value="comments" className="gap-1.5">
						<MessageSquare className="h-4 w-4" />
						Comments ({roast.comments?.length ?? 0})
					</TabsTrigger>
					<TabsTrigger value="rewrites" className="gap-1.5">
						<GitFork className="h-4 w-4" />
						Rewrites ({roast.rewrites?.length ?? 0})
					</TabsTrigger>
				</TabsList>

				<TabsContent value="comments" className="space-y-4">
					{/* Comment input */}
					<Card className="border-border/40 bg-card/50">
						<CardContent className="p-4">
							<Textarea
								placeholder="Share your analysis or suggestion..."
								value={comment}
								onChange={(e) => setComment(e.target.value)}
								rows={3}
								className="mb-3 resize-none"
							/>
							<Button size="sm" onClick={handleComment} disabled={!comment.trim() || submitting} className="gap-1.5">
								<Send className="h-3.5 w-3.5" />
								Comment
							</Button>
						</CardContent>
					</Card>

					{/* Comments list */}
					{(roast.comments ?? []).map((c) => (
						<Card key={c.id} className={`border-border/40 bg-card/50 ${c.parentId ? "ml-8" : ""}`}>
							<CardContent className="p-4">
								<div className="flex items-start justify-between">
									<button
										onClick={() => c.user && navigate({ name: "profile", id: c.userId })}
										className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
									>
										<div className="flex h-5 w-5 items-center justify-center rounded-full bg-orange-500/20 text-[10px] font-bold text-orange-500">
											{c.user?.username?.[0]?.toUpperCase() ?? "?"}
										</div>
										<span className="font-medium">{c.user?.username}</span>
										<span className="text-muted-foreground">·</span>
										<span>{timeAgo(c.createdAt)}</span>
									</button>
									<VoteButton targetId={c.id} targetType="comment" initialCount={c.upvotes} size="sm" />
								</div>
								<p className="mt-2 text-sm leading-relaxed text-foreground/85">{c.content}</p>
							</CardContent>
						</Card>
					))}
				</TabsContent>

				<TabsContent value="rewrites" className="space-y-4">
					{/* Rewrite input */}
					<Card className="border-border/40 bg-card/50">
						<CardHeader className="pb-3">
							<CardTitle className="flex items-center gap-2 text-sm">
								<GitFork className="h-4 w-4" />
								{forkingFrom ? "Fork this rewrite" : "Submit a rewrite"}
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-3 pt-0">
							{forkingFrom && (
								<div className="rounded-md bg-muted/30 p-3">
									<p className="mb-1 text-xs text-muted-foreground">Forking from:</p>
									<p className="text-sm italic text-foreground/70">"{forkOriginal}"</p>
									<Button variant="ghost" size="sm" onClick={() => { setForkingFrom(null); setForkOriginal(""); setRewriteCopy(""); }} className="mt-1 h-6 text-xs">
										Cancel fork
									</Button>
								</div>
							)}
							<Textarea
								placeholder="Your rewritten copy..."
								value={rewriteCopy}
								onChange={(e) => setRewriteCopy(e.target.value)}
								rows={4}
								className="resize-none"
							/>
							<Textarea
								placeholder="Explain what you changed and why..."
								value={rewriteExplanation}
								onChange={(e) => setRewriteExplanation(e.target.value)}
								rows={2}
								className="resize-none"
							/>
							<Button
								size="sm"
								onClick={forkingFrom ? handleFork : handleRewrite}
								disabled={!rewriteCopy.trim() || !rewriteExplanation.trim() || submitting}
								className="gap-1.5"
							>
								<GitFork className="h-3.5 w-3.5" />
								{forkingFrom ? "Publish Fork" : "Publish Rewrite"}
							</Button>
						</CardContent>
					</Card>

					{/* Rewrites list */}
					{(roast.rewrites ?? []).map((rw) => (
						<Card key={rw.id} className="border-border/40 bg-card/50">
							<CardContent className="p-4 space-y-3">
								<div className="flex items-start justify-between">
									<button
										onClick={() => rw.user && navigate({ name: "profile", id: rw.userId })}
										className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
									>
										<div className="flex h-5 w-5 items-center justify-center rounded-full bg-orange-500/20 text-[10px] font-bold text-orange-500">
											{rw.user?.username?.[0]?.toUpperCase() ?? "?"}
										</div>
										<span className="font-medium">{rw.user?.username}</span>
										<span className="text-muted-foreground">·</span>
										<span>{timeAgo(rw.createdAt)}</span>
									</button>
									<div className="flex items-center gap-1">
										<Button
											variant="ghost"
											size="sm"
											onClick={() => startFork(rw.id, rw.rewrittenCopy)}
											className="h-7 gap-1 text-xs"
										>
											<GitFork className="h-3 w-3" />
											Fork
										</Button>
										<VoteButton targetId={rw.id} targetType="rewrite" initialCount={rw.upvotes} size="sm" />
									</div>
								</div>

								{rw.parentRewriteId && (
									<Badge variant="outline" className="text-xs text-muted-foreground">
										Forked from another rewrite
									</Badge>
								)}

								<div className="space-y-2 rounded-md bg-muted/20 p-3">
									<p className="text-xs font-medium text-muted-foreground">Original:</p>
									<p className="text-sm italic text-foreground/50 line-through">
										{rw.originalCopy}
									</p>
								</div>

								<div className="space-y-2 rounded-md border border-emerald-500/20 bg-emerald-500/5 p-3">
									<p className="text-xs font-medium text-emerald-400">Rewrite:</p>
									<p className="text-sm text-foreground/90">{rw.rewrittenCopy}</p>
								</div>

								<div className="flex items-start gap-2 text-sm">
									<Lightbulb className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-400" />
									<p className="text-muted-foreground">{rw.explanation}</p>
								</div>
							</CardContent>
						</Card>
					))}
				</TabsContent>
			</Tabs>
		</div>
	);
}
